/**
 * useTeamPermissions — client-side permission derivation for the Teams feature.
 *
 * Per TEAMS-SPEC.md (TM, Apr 24 2026):
 *   - System/Tenant admin → can create teams in any department.
 *   - Department head (Department.headId === user.id) → can create teams only
 *     in their own department.
 *
 * Source of truth for "which depts do I head":
 *   `user.headOfDepartmentIds: string[]` — added by /auth/me backend augmentation.
 *   If the backend payload does not yet include it (deploy race), we degrade
 *   gracefully: non-admin users simply see no "create team" affordance.
 *
 * Super admin detection: `user.isSuperAdmin` (preferred) OR the legacy
 * `admin@aura.ardhanex.com` email match — kept for back-compat until every
 * deployment carries the flag.
 */
import { useAuthStore } from '@/stores/auth';

const SUPER_ADMIN_EMAIL = 'admin@aura.ardhanex.com';

export interface TeamPermissions {
  /** Platform super-admin (email === SUPER_ADMIN_EMAIL OR user.isSuperAdmin). */
  isSuperAdmin: boolean;
  /** Tenant-level admin (role.name === 'admin' | 'owner') — includes super-admin. */
  isAdmin: boolean;
  /** True if the current user can create teams in ANY department. Admin-only. */
  canCreateAny: boolean;
  /** True if the user can create a team in the given deptId.
   *  If `deptId` is omitted, returns true when the user can create in at
   *  least one department (admin OR dept-head of anything). */
  canCreateInDept: boolean;
  /** True if the user can edit/rename teams in the given deptId. */
  canEdit: boolean;
  /** True if the user can delete (soft) teams — admin only. */
  canDelete: boolean;
  /** All department ids where the user is the head. */
  myDeptHeadOf: string[];
}

export function useTeamPermissions(deptId?: string): TeamPermissions {
  const user = useAuthStore((s) => s.user);

  const roleName = (user?.role?.name || '').toLowerCase();
  const isSuperAdmin =
    !!user?.isSuperAdmin || (user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL);
  const isAdmin = isSuperAdmin || roleName === 'admin' || roleName === 'owner';
  const headOf: string[] = user?.headOfDepartmentIds ?? [];

  const canCreateAny = isAdmin;
  const canCreateInDept = deptId
    ? isAdmin || headOf.includes(deptId)
    : isAdmin || headOf.length > 0;

  return {
    isSuperAdmin,
    isAdmin,
    canCreateAny,
    canCreateInDept,
    canEdit: canCreateInDept,
    canDelete: isAdmin,
    myDeptHeadOf: headOf,
  };
}
